import React, { useContext, useEffect, useState } from 'react'
import styles from './styles.module.css'
import { Checkbox, Input, Select } from '../../../components/ModalInputs'
import { useDispatch, useSelector } from 'react-redux'
import { ClassingContext } from '../../../utils/context'
import { createGroups, getGroupInfo, getPositionHistory, markCurator, updateGroups } from '../../../actions/groups'
import { useFormik } from 'formik'
import Button from '../../../components/Button'
import ModalFooter from '../../../components/ModalFooter/ModalFooter'
import { createNotification } from '../../../components/Notifications/Notifications'
import { getStudySteps } from '../../../actions/common'
import { BiCalendar } from 'react-icons/bi'
import Confirmation from '../../../components/Confirmation/Confirmation'
import moment from 'moment'
import Table from '../../../components/Table'

export default function Main() {

  const dispatch = useDispatch()
  const { classId, closeModal, infoCreateChange } = useContext(ClassingContext)
  const { groupInfo, positionHistory, curatorCandidates, modalLoading: loading } = useSelector(state => state.groups)
  const {
    schoolYears,
    specializations,
    languages,
    studyProfiles,
    studyModes,
    studyForms,
    studySteps,
    shifts = [],
    teachers
  } = useSelector(state => state.common)

  // pos: 0=классный руководитель, 1..3=воспитатели
  const [posHistoryModal, setPosHistoryModal] = useState(null)
  const [curatorConfirm, setCuratorConfirm] = useState(null)
  // Очередь остальных кандидатов — бэк может вернуть сразу несколько
  // (классный руководитель + до 3 воспитателей), их всех нужно спросить по очереди
  const [candidateQueue, setCandidateQueue] = useState([])

  useEffect(() => {
    if (classId && classId !== 'add') {
      dispatch(getGroupInfo(classId))
    } else {
      dispatch({ type: 'SET_GROUP_INFO', payload: null })
    }
    dispatch(getStudySteps())
  }, [classId])

  // Показываем подтверждение куратора, когда появились кандидаты — ставим
  // первого в очередь на подтверждение, остальных откладываем в candidateQueue
  useEffect(() => {
    if (curatorCandidates?.length) {
      setCuratorConfirm(curatorCandidates[0])
      setCandidateQueue(curatorCandidates.slice(1))
    }
  }, [curatorCandidates])

  // Перейти к следующему кандидату в очереди либо закрыть модалку, если очередь пуста
  const processNextCandidate = () => {
    setCandidateQueue(prev => {
      if (prev.length) {
        setCuratorConfirm(prev[0])
        return prev.slice(1)
      }
      setCuratorConfirm(null)
      dispatch({ type: 'SET_GROUP_CURATOR_CANDIDATES', payload: null })
      closeModal()
      return []
    })
  }

  const openPosHistory = (pos) => {
    dispatch(getPositionHistory(classId, pos))
    setPosHistoryModal(pos)
  }

  const intOrNull = (v) => (v === '' || v === null || v === undefined) ? null : Number(v)

  const sanitizeValues = (v) => ({
    ...v,
    class_teacher: intOrNull(v.class_teacher),
    class_teacher2: intOrNull(v.class_teacher2),
    class_teacher3: intOrNull(v.class_teacher3),
    class_teacher4: intOrNull(v.class_teacher4),
    shift: intOrNull(v.shift),
    f_studyform: intOrNull(v.f_studyform),
    f_studysteps: intOrNull(v.f_studysteps),
    track_profile_id: intOrNull(v.track_profile_id),
  })

  const handleCreateClass = async () => {
    if (!values.name || !values.liter || values.year === undefined) {
      createNotification('error', 'Заполните все обязательные поля (Название, Литера, Год обучения)')
      return
    }
    const resp = await dispatch(createGroups(sanitizeValues(values)))
    if (resp && !resp.curator_candidates?.length) closeModal()
  }

  const handleUpdateClass = async () => {
    if (!values.name || !values.liter || values.year === undefined) {
      createNotification('error', 'Заполните все обязательные поля (Название, Литера, Год обучения)')
      return
    }
    const resp = await dispatch(updateGroups(classId, sanitizeValues(values)))
    if (resp && !resp.curator_candidates?.length) closeModal()
  }

  const {
    values,
    handleChange,
    handleSubmit
  } = useFormik({
    initialValues: {
      owner_gid: 0,
      name: '',
      shortname: '',
      year: 1,
      liter: '',
      rota: '',
      vzvod: '',
      kurs: '',
      class_teacher: '',
      class_teacher2: '',
      class_teacher3: '',
      class_teacher4: '',
      yeargraduation: 0,
      specialization: '',
      language: '',
      study_mode: '',
      shift: '',
      ind_period: 0,
      track_profile_id: '',
      f_studyform: '',
      f_studysteps: '',
      ...groupInfo,
    },
    enableReinitialize: true,
    onSubmit: classId === 'add' ? handleCreateClass : handleUpdateClass
  })

  const acceptCuratorConfirm = () => {
    dispatch(markCurator(classId, curatorConfirm.mid))
    processNextCandidate()
  }
  const rejectCuratorConfirm = () => {
    processNextCandidate()
  }

  const teacherLabel = (mid) => teachers?.find(t => t.mid === mid)?.calc_fio || ''

  return (
    <>
      {curatorConfirm && (
        <Confirmation
          top="30px"
          left="40px"
          title={`Отметить в учебной структуре для ${curatorConfirm.fio} курируемый класс и курируемые предметы?`}
          acceptConfirmation={acceptCuratorConfirm}
          rejectConfirmation={rejectCuratorConfirm}
        />
      )}

      {posHistoryModal !== null && (
        <div className={styles.posHistoryOverlay}>
          <div className={styles.posHistoryModal}>
            <h3>История назначений</h3>
            {positionHistory?.length
              ? (
                <Table>
                  <thead>
                    <tr>
                      <th>Должность занимает</th>
                      <th>с</th>
                      <th>по</th>
                    </tr>
                  </thead>
                  <tbody>
                    {positionHistory.map(({ id, fio, date_from, date_to }) => (
                      <tr key={id}>
                        <td>{fio}</td>
                        <td>{date_from ? moment(date_from).format('DD.MM.YYYY') : ''}</td>
                        <td>{date_to ? moment(date_to).format('DD.MM.YYYY') : 'по сей день'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )
              : <p>Нет записей</p>
            }
            <Button onClick={() => setPosHistoryModal(null)}>Закрыть</Button>
          </div>
        </div>
      )}

      <div className={styles.modalMain}>
        <div className={styles.tabWrapper}>

          {/* Блок 1: основные поля */}
          <div className={styles.formGrid}>
            <div className={styles.gridCell}><Input label='Название *' value={values.name} onChange={handleChange} name='name' /></div>
            <div className={styles.gridCell}><Input label='Короткое название' value={values.shortname} onChange={handleChange} name='shortname' /></div>
            <div className={styles.gridCell}>
              <Select label='Год обучения *' value={values.year} onChange={handleChange} name='year'>
                {[0,1,2,3,4,5,6,7,8,9,10,11,12].map(el => <option key={el} value={el}>{el}</option>)}
              </Select>
            </div>
            <div className={styles.gridCell}><Input label='Литера *' value={values.liter} onChange={handleChange} name='liter' /></div>
            <div className={styles.gridCell}><Input label='Рота' value={values.rota} onChange={handleChange} name='rota' /></div>
            <div className={styles.gridCell}><Input label='Взвод' value={values.vzvod} onChange={handleChange} name='vzvod' /></div>
            <div className={styles.gridCell}><Input label='Курс' value={values.kurs} onChange={handleChange} name='kurs' /></div>
          </div>

          {/* Блок 2: преподаватели */}
          <div className={styles.formGrid2}>
            <div>
              <div className={styles.teacherRow}>
                <Select label='Классный руководитель' value={values.class_teacher} onChange={handleChange} name='class_teacher'>
                  <option value=''>— не выбран —</option>
                  {teachers?.map(({ calc_fio, mid }) => <option key={mid} value={mid}>{calc_fio}</option>)}
                </Select>
                {classId !== 'add' && (
                  <button type='button' className={styles.calendarBtn} title='История назначений' onClick={() => openPosHistory(0)}>
                    <BiCalendar />
                  </button>
                )}
              </div>
            </div>
            <div>
              <label className={styles.labelText}>Воспитатели</label>
              {[1, 2, 3].map((pos) => (
                <div key={pos} className={styles.teacherRow}>
                  <Select value={values[`class_teacher${pos + 1}`]} onChange={handleChange} name={`class_teacher${pos + 1}`}>
                    <option value=''>— не выбран —</option>
                    {teachers?.map(({ calc_fio, mid }) => <option key={mid} value={mid}>{calc_fio}</option>)}
                  </Select>
                  {classId !== 'add' && (
                    <button type='button' className={styles.calendarBtn} title='История назначений' onClick={() => openPosHistory(pos)}>
                      <BiCalendar />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Блок 3: справочники */}
          <div className={styles.formGrid}>
            <div className={styles.gridCell}>
              <Select label='Выпущен в учебном году' value={values.yeargraduation} onChange={handleChange} name='yeargraduation'>
                <option value='0'></option>
                {schoolYears?.map(({ xp_key, name }) => <option key={xp_key} value={xp_key}>{name}</option>)}
              </Select>
            </div>
            <div className={styles.gridCell}>
              <Select label='Специализация' value={values.specialization} onChange={handleChange} name='specialization'>
                <option value=''>— не выбрана —</option>
                {specializations?.map(v => <option key={v}>{v}</option>)}
              </Select>
            </div>
            <div className={styles.gridCell}>
              <Select label='Язык обучения' value={values.language} onChange={handleChange} name='language'>
                <option value=''>— не выбран —</option>
                {languages?.map(v => <option key={v}>{v}</option>)}
              </Select>
            </div>
            <div className={styles.gridCell}>
              <Select label='Режим обучения' value={values.study_mode} onChange={handleChange} name='study_mode'>
                <option value=''>— не выбран —</option>
                {studyModes?.map(v => <option key={v}>{v}</option>)}
              </Select>
            </div>
            <div className={styles.gridCell}>
              <Select label='Смена' value={values.shift} onChange={handleChange} name='shift'>
                <option value=''>— не выбрана —</option>
                {shifts?.map(({ sid, name }) => <option key={sid} value={sid}>{name}</option>)}
              </Select>
            </div>
            <div className={styles.gridCell}>
              <Select label='Профиль' value={values.track_profile_id} onChange={handleChange} name='track_profile_id'>
                <option value=''>— не указан —</option>
                {studyProfiles?.map(({ trp_id, name }) => <option key={trp_id} value={trp_id}>{name}</option>)}
              </Select>
            </div>
            <div className={styles.gridCell}>
              <Select label='Форма обучения' value={values.f_studyform} onChange={handleChange} name='f_studyform'>
                <option value=''>— не выбрана —</option>
                {studyForms?.map(({ f_studyform, name }) => <option key={f_studyform} value={f_studyform}>{name}</option>)}
              </Select>
            </div>
            <div className={styles.gridCell}>
              <Select label='Ступень обучения' value={values.f_studysteps} onChange={handleChange} name='f_studysteps'>
                <option value=''>— не выбрана —</option>
                {studySteps?.map(({ f_studysteps, name }) => <option key={f_studysteps} value={f_studysteps}>{name}</option>)}
              </Select>
            </div>
            <div className={styles.checkboxCell}>
              <Checkbox label="Индивидуальный распорядок" name='ind_period' value={values.ind_period} onChange={handleChange} />
            </div>
          </div>

          {classId !== 'add' && (groupInfo?.whocreate_fio || groupInfo?.whochange_fio) && (
            <div className={styles.auditRow}>
              {groupInfo.whocreate_fio && (
                <span>Создал: {groupInfo.whocreate_fio}{groupInfo.whencreate ? ` (${moment(groupInfo.whencreate).format('DD.MM.YYYY')})` : ''}</span>
              )}
              {groupInfo.whochange_fio && (
                <span>Изменил: {groupInfo.whochange_fio}{groupInfo.whenchange ? ` (${moment(groupInfo.whenchange).format('DD.MM.YYYY')})` : ''}</span>
              )}
            </div>
          )}
        </div>
      </div>
      <ModalFooter closeModal={closeModal} handleSubmit={handleSubmit} infoCreateChange={infoCreateChange} />
    </>
  )
}
